import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Import the enum from the database package
// Note: Adjust import path based on your project structure
export enum SubdivisionType {
  STATE = 'STATE',
  PROVINCE = 'PROVINCE',
  TERRITORY = 'TERRITORY',
  DISTRICT = 'DISTRICT',
  REGION = 'REGION',
  CANTON = 'CANTON',
  PREFECTURE = 'PREFECTURE',
}

interface SubdivisionTypeChipProps {
  subdivisionType: SubdivisionType;
  className?: string;
}

const getSubdivisionTypeDisplay = (subdivisionType: SubdivisionType): string => {
  switch (subdivisionType) {
    case SubdivisionType.STATE:
      return 'State';
    case SubdivisionType.PROVINCE:
      return 'Province';
    case SubdivisionType.TERRITORY:
      return 'Territory';
    case SubdivisionType.DISTRICT:
      return 'District';
    case SubdivisionType.REGION:
      return 'Region';
    case SubdivisionType.CANTON:
      return 'Canton';
    case SubdivisionType.PREFECTURE:
      return 'Prefecture';
    default:
      return String(subdivisionType);
  }
};

const getSubdivisionTypeVariant = (
  subdivisionType: SubdivisionType
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (subdivisionType) {
    case SubdivisionType.STATE:
      return 'default'; // Green - most common
    case SubdivisionType.PROVINCE:
      return 'secondary'; // Blue - common federal system
    case SubdivisionType.TERRITORY:
      return 'outline'; // Gray - special administrative areas
    case SubdivisionType.DISTRICT:
      return 'secondary'; // Blue - administrative regions
    case SubdivisionType.REGION:
      return 'outline'; // Gray - large administrative areas
    case SubdivisionType.CANTON:
      return 'default'; // Green - Swiss/European system
    case SubdivisionType.PREFECTURE:
      return 'secondary'; // Blue - Japanese system
    default:
      return 'outline';
  }
};

const getSubdivisionTypeColorClasses = (variant: string) => {
  switch (variant) {
    case 'default':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800';
    case 'secondary':
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800';
    case 'outline':
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-800';
    case 'destructive':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
    default:
      return '';
  }
};

export const SubdivisionTypeChip = ({ subdivisionType, className }: SubdivisionTypeChipProps) => {
  const variant = getSubdivisionTypeVariant(subdivisionType);
  const displayText = getSubdivisionTypeDisplay(subdivisionType);
  const colorClasses = getSubdivisionTypeColorClasses(variant);

  return (
    <Badge variant={variant} className={cn('text-xs font-medium', colorClasses, className)}>
      {displayText}
    </Badge>
  );
};

// Export utility functions for consistency
